/**
 * Server-side chat history of the HaulierMagic CoPilot (CU-14zefhy1qnh, item 10; twin of the RouteMagic one).
 * Each chat session's transcript is stored as `transcript.json` in the caller's PythonMagic session folder (the API
 * proxies it). The browser keeps localStorage as an offline cache and merges: the newer side wins per session.
 * Pure helpers live here so they can be unit tested without the component.
 */

export const TRANSCRIPT_FILE = 'transcript.json';
export const TRANSCRIPT_VERSION = 1;
export const MAX_TRANSCRIPT_MESSAGES = 200;
export const MAX_TRANSCRIPT_BYTES = 1_000_000;
/** closed session keys remembered so a server copy does not bring a closed tab back */
export const MAX_CLOSED_KEYS = 100;
/** debounce of transcript uploads after a change */
export const TRANSCRIPT_SAVE_DEBOUNCE_MS = 2000;

/** Same rule as the session keys the chat creates ("s" + epoch ms); anything else from the server is ignored. */
const SESSION_KEY = /^[A-Za-z0-9_-]{1,64}$/;

export interface ServerSessionInfo {
    key: string;
    label: string;
    updatedAt: number;
    /** RMA-27: server-issued version of the stored transcript (absent on an API without it) */
    revision?: string;
    /** CU-14zefhy38pt: pinned by the person */
    pinned?: boolean;
    /** CU-14zefhy38pt: past the 50 newest unpinned chats; the server lists it only with `includeArchived` or a search */
    archived?: boolean;
}

export interface Transcript {
    version: number;
    key: string;
    label: string;
    updatedAt: number;
    messages: any[];
    /**
     * RMA-27: the server revision this browser last saw, sent as the HM API's `baseRev` (a JSON number; the API ignores a
     * string); the server refuses ({saved: false}) a save based on another one
     */
    baseRev?: number | string;
    /** RMA-27: the server's revision of the transcript just read */
    revision?: string;
    /** CU-14zefhy38pt W2: pinned by the person (listed first in the history) */
    pinned?: boolean;
    /** CU-14zefhy38pt W3: the chat this one was branched from */
    forkedFrom?: string;
}

/** Minimal shape of a chat session these helpers need. */
export interface HistorySession {
    key: string;
    label: string;
    messages: any[];
    updatedAt?: number;
    /** RMA-27: server revision this browser last saw or saved for the session */
    revision?: string;
    /** RMA-27: changed here and not uploaded yet (kept in the browser cache so a reload does not forget it) */
    unsynced?: boolean;
    pinned?: boolean;
    forkedFrom?: string;
}

/** A server revision (number or string) as a string; undefined when absent. */
export function toRevision(value: any): string | undefined {
    if (typeof value === 'number' && isFinite(value)) {
        return String(value);
    }
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * CU-14zefhy29pp (T5): the revision a server answer carries. The HM API names it `rev` (session rows, GET transcript,
 * PUT answer); `revision` is still read for an API that uses the longer name.
 */
export function readRevision(item: any): string | undefined {
    if (!item || typeof item !== 'object') {
        return undefined;
    }
    return toRevision(item.rev) ?? toRevision(item.revision);
}

/** CU-14zefhy29pp (T5): a revision as the API's `baseRev`: a number when it is one (the API reads only numbers). */
export function toBaseRev(revision: string | undefined): number | string | undefined {
    const r = toRevision(revision);
    if (r === undefined) {
        return undefined;
    }
    return /^\d+$/.test(r) && Number.isSafeInteger(Number(r)) ? Number(r) : r;
}

/** `transcript.json` (at the session root or under any folder) is history, not a file the user attached. */
export function isTranscriptFile(file: any): boolean {
    const path = file && typeof file === 'object' ? String(file.path || file.name || '') : String(file || '');
    const name = path.includes('/') ? path.substring(path.lastIndexOf('/') + 1) : path;
    return name === TRANSCRIPT_FILE;
}

/** Epoch ms from a number or an ISO string; 0 when unreadable. */
export function toEpochMs(value: any): number {
    if (typeof value === 'number' && isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim()) {
        const n = Number(value);
        if (isFinite(n) && /^\d+$/.test(value.trim())) {
            return n;
        }
        const t = Date.parse(value);
        return isNaN(t) ? 0 : t;
    }
    return 0;
}

/** A `GET sessions` entry that is a tombstone (the chat was closed on some device), not a live chat. */
function isTombstone(item: any): boolean {
    return !!item && typeof item === 'object' && (item.deleted === true || item.tombstone === true);
}

/** Normalises `GET sessions`: valid keys only, de-duplicated (newest wins); tombstones are not sessions. */
export function parseServerSessions(raw: any): ServerSessionInfo[] {
    const list = Array.isArray(raw) ? raw : Array.isArray(raw?.sessions) ? raw.sessions : [];
    const byKey = new Map<string, ServerSessionInfo>();
    for (const item of list) {
        const key = item && typeof item === 'object' ? String(item.key ?? item.session ?? '') : '';
        if (!SESSION_KEY.test(key) || isTombstone(item)) {
            continue;
        }
        const info: ServerSessionInfo = {key, label: String(item.label || '').trim(), updatedAt: toEpochMs(item.updatedAt)};
        const revision = readRevision(item);
        if (revision !== undefined) {
            info.revision = revision;
        }
        if (item.pinned === true) {
            info.pinned = true;
        }
        if (item.archived === true) {
            info.archived = true;
        }
        const prev = byKey.get(key);
        if (!prev || info.updatedAt > prev.updatedAt) {
            byKey.set(key, info);
        }
    }
    return Array.from(byKey.values());
}

/**
 * X3 (CU-14zefhy1r83): the chats closed on any device, from `GET sessions?includeDeleted=true`, as `{key, updatedAt}`
 * with `updatedAt` = when it was closed (0 = unknown). The API lists each tombstone as `{key, updatedAt, messages: 0,
 * deleted: true}`; a top-level `deleted` / `deletedKeys` / `tombstones` array (of keys or `{key, updatedAt}` objects)
 * is read too. Valid keys only, de-duplicated (latest close wins).
 */
export function parseDeletedSessions(raw: any): ServerSessionInfo[] {
    const out = new Map<string, ServerSessionInfo>();
    const add = (item: any) => {
        const key = typeof item === 'string' ? item : item && typeof item === 'object' ? String(item.key ?? item.session ?? '') : '';
        if (!SESSION_KEY.test(key)) {
            return;
        }
        const updatedAt = item && typeof item === 'object' ? toEpochMs(item.updatedAt ?? item.deletedAt) : 0;
        const prev = out.get(key);
        if (!prev || updatedAt > prev.updatedAt) {
            out.set(key, {key, label: '', updatedAt});
        }
    };
    const list = Array.isArray(raw) ? raw : Array.isArray(raw?.sessions) ? raw.sessions : [];
    list.filter(isTombstone).forEach(add);
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        for (const field of ['deleted', 'deletedKeys', 'tombstones']) {
            if (Array.isArray(raw[field])) {
                raw[field].forEach(add);
            }
        }
    }
    return Array.from(out.values());
}

/** X3: the server refused a transcript upload because the chat was closed on another device (it stays closed). */
export function isDeletedOnServer(result: any): boolean {
    if (!result || typeof result !== 'object') {
        return false;
    }
    if (result.status === 410) {
        return true; // an HttpErrorResponse "Gone"
    }
    const body = result.error && typeof result.error === 'object' ? result.error : result;
    return body.deleted === true || body.tombstone === true;
}

function byteSize(s: string): number {
    if (typeof TextEncoder !== 'undefined') {
        return new TextEncoder().encode(s).length;
    }
    return s.length * 2; // conservative when no encoder
}

/**
 * What gets stored for one session: finished messages only (no in-flight placeholder), last 200, and the oldest
 * dropped until the JSON is at most 1 MB. Transient session state (queue, poll timer, consent) is never stored.
 */
export function toTranscript(session: HistorySession, now: number = Date.now()): Transcript {
    let messages = (session.messages || []).filter((m) => m && !m.pending);
    if (messages.length > MAX_TRANSCRIPT_MESSAGES) {
        messages = messages.slice(messages.length - MAX_TRANSCRIPT_MESSAGES);
    }
    // deep copy so later edits of the live messages never change what was queued for upload
    messages = JSON.parse(JSON.stringify(messages));
    const transcript: Transcript = {
        version: TRANSCRIPT_VERSION,
        key: session.key,
        label: session.label,
        updatedAt: session.updatedAt || now,
        messages
    };
    // always written: an absent flag would keep a stale pin on another device after an unpin
    transcript.pinned = !!session.pinned;
    if (session.forkedFrom) {
        transcript.forkedFrom = session.forkedFrom;
    }
    const base = toBaseRev(session.revision);
    if (base !== undefined) {
        transcript.baseRev = base;
    }
    let size = byteSize(JSON.stringify(transcript));
    while (size > MAX_TRANSCRIPT_BYTES && transcript.messages.length) {
        // drop roughly the share that is over, at least one message
        const drop = Math.max(1, Math.ceil(transcript.messages.length * (1 - MAX_TRANSCRIPT_BYTES / size)));
        transcript.messages = transcript.messages.slice(drop);
        size = byteSize(JSON.stringify(transcript));
    }
    return transcript;
}

/** Reads a stored transcript; null when it is not one (wrong shape / other session). */
export function fromTranscript(raw: any, key: string): Transcript | null {
    let t = raw;
    if (typeof t === 'string') {
        try {
            t = JSON.parse(t);
        } catch (e) {
            return null;
        }
    }
    if (!t || typeof t !== 'object' || !Array.isArray(t.messages)) {
        return null;
    }
    if (t.key && String(t.key) !== key) {
        return null;
    }
    const messages = t.messages
        .filter((m: any) => m && typeof m === 'object' && (m.role === 'user' || m.role === 'assistant') && !m.pending)
        .slice(-MAX_TRANSCRIPT_MESSAGES);
    const read: Transcript = {
        version: Number(t.version) || TRANSCRIPT_VERSION,
        key,
        label: typeof t.label === 'string' ? t.label : '',
        updatedAt: toEpochMs(t.updatedAt),
        messages
    };
    const revision = readRevision(t);
    if (revision !== undefined) {
        read.revision = revision;
    }
    if (typeof t.pinned === 'boolean') {
        read.pinned = t.pinned;
    }
    if (typeof t.forkedFrom === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(t.forkedFrom)) {
        read.forkedFrom = t.forkedFrom;
    }
    return read;
}

export interface MergePlan {
    /** server sessions to download: not known here, or newer on the server */
    fetch: string[];
    /** local sessions to upload: unknown to the server, or newer here (only sessions with messages) */
    upload: string[];
    /** X3: local sessions the server reports closed on another device; they are dropped here, never uploaded */
    drop: string[];
}

/**
 * Decides per session which side wins. Closed keys (this browser's tombstones) are neither fetched nor uploaded.
 * X3: a chat the server reports closed (`deleted`, see parseDeletedSessions) is dropped here when it was closed at or
 * after its last local change (deletes win across devices; the server refuses that upload too); a local copy changed
 * after the close is the chat used again and uploads as usual (the server then replaces its tombstone).
 * Equal timestamps = in sync.
 */
export function mergePlan(
    local: HistorySession[],
    server: ServerSessionInfo[],
    closedKeys: string[] = [],
    deleted: ServerSessionInfo[] = []
): MergePlan {
    const localKeys = new Set((local || []).map((s) => s.key));
    const drop: string[] = [];
    const closed = new Set(closedKeys || []);
    (deleted || []).forEach((tomb) => {
        const mine = (local || []).find((s) => s.key === tomb.key);
        if (!mine || !tomb.updatedAt || (mine.updatedAt || 0) <= tomb.updatedAt) {
            closed.add(tomb.key);
            if (localKeys.has(tomb.key) && !drop.includes(tomb.key)) {
                drop.push(tomb.key);
            }
        }
    });
    const localByKey = new Map((local || []).map((s) => [s.key, s] as [string, HistorySession]));
    const serverByKey = new Map((server || []).map((s) => [s.key, s] as [string, ServerSessionInfo]));
    const fetch: string[] = [];
    const upload: string[] = [];
    // RMA-27: with a server revision on both sides, "who changed" is decided by revisions and the unsynced flag, not by
    // comparing the browser's clock with the server's; the clocks only break a real conflict (both sides changed).
    const hasRevisions = (info: ServerSessionInfo, mine: HistorySession) => info.revision !== undefined && mine.revision !== undefined;
    serverByKey.forEach((info, key) => {
        if (closed.has(key)) {
            return;
        }
        const mine = localByKey.get(key);
        if (!mine) {
            fetch.push(key);
        } else if (hasRevisions(info, mine)) {
            const serverChanged = info.revision !== mine.revision;
            if (serverChanged && (!mine.unsynced || info.updatedAt > (mine.updatedAt || 0))) {
                fetch.push(key);
            }
        } else if (info.updatedAt > (mine.updatedAt || 0)) {
            fetch.push(key);
        }
    });
    localByKey.forEach((mine, key) => {
        if (closed.has(key) || !(mine.messages || []).some((m) => m && !m.pending)) {
            return;
        }
        const info = serverByKey.get(key);
        if (!info) {
            upload.push(key);
        } else if (hasRevisions(info, mine)) {
            const serverChanged = info.revision !== mine.revision;
            if (mine.unsynced && (!serverChanged || (mine.updatedAt || 0) >= info.updatedAt)) {
                upload.push(key);
            }
        } else if ((mine.updatedAt || 0) > info.updatedAt) {
            upload.push(key);
        }
    });
    return {fetch, upload, drop};
}

/** Adds a closed key to the tombstone list (newest last, capped). */
export function addClosedKey(list: string[], key: string): string[] {
    const next = (list || []).filter((k) => k !== key);
    next.push(key);
    return next.slice(-MAX_CLOSED_KEYS);
}

/** Label for a session that only exists on the server and has none stored. */
export function serverSessionLabel(info: {label?: string}, index: number, taken: string[] = []): string {
    return (info.label || '').trim() || freeSessionLabel(taken, index);
}

/**
 * CU-14zefhy29pp (X1): "Session N" for a new tab with N >= `from` and no open tab already labelled so (labels by count
 * alone gave two different chats the same name once a kept tab and a server chat met).
 */
export function freeSessionLabel(taken: string[], from: number): string {
    const used = new Set((taken || []).map((l) => String(l || '').trim()));
    let n = Math.max(1, Math.floor(from) || 1);
    while (used.has(`Session ${n}`)) {
        n++;
    }
    return `Session ${n}`;
}

/**
 * CU-14zefhy29pp (X1): the label of a chat that arrives from the server next to the open tabs. A clash with an open tab
 * that has no finished message (a new tab, never saved) renames that tab; a clash with a used tab gives the arriving
 * chat the next free "Session N".
 */
export function placeServerLabel(
    wanted: string,
    tabs: {key: string; label: string; messages: any[]}[],
    from: number
): {label: string; rename?: {key: string; label: string}} {
    const labels = (tabs || []).map((t) => t.label);
    const clash = (tabs || []).find((t) => String(t.label || '').trim() === wanted);
    if (!clash) {
        return {label: wanted};
    }
    if (!(clash.messages || []).some((m) => m && !m.pending)) {
        return {label: wanted, rename: {key: clash.key, label: freeSessionLabel(labels.concat(wanted), from)}};
    }
    return {label: freeSessionLabel(labels, from)};
}
